import { type ReactNode } from 'react'
import { Link } from 'react-router'

import { Callout } from '#app/components/ui/callout.tsx'

export default function PrivacyRoute() {
	return (
		<div className="container mx-auto max-w-3xl py-6 md:pb-12">
			<header className="mb-6 space-y-1">
				<Title>Privacy Policy</Title>
				<p className="text-muted-foreground text-sm">
					Last Updated: November 13, 2025
				</p>
			</header>

			<div className="prose flex max-w-none flex-col gap-y-6">
				<p className="text-sm leading-relaxed">
					This Privacy Policy explains how CurigenX, Inc. DBA (Doing Business
					As) Lydra AI ("Lydra," "we," "us," "our") collects, uses, shares, and
					protects personal information in connection with the Lydra AI
					pharmacovigilance platform, websites, voice channels, APIs, and
					related services (collectively, the "Services"). Your use of the
					Services is also subject to our{' '}
					<Link to="/terms-of-service" className="text-blue-600">
						Terms of Service
					</Link>
					.
				</p>

				<Section title="1. Purpose and Scope">
					<p>
						We process personal information (a) as a controller when we
						determine why and how data is processed (for example, for account
						management, product analytics, or marketing), and (b) as a processor
						or HIPAA Business Associate when we handle protected health
						information (PHI) on behalf of our enterprise customers. When we act
						as a processor, our customers are responsible for providing privacy
						notices to individuals and honoring their rights. This policy
						describes our controller activities and the support we provide to
						customers for processor activities.
					</p>
				</Section>

				<Section title="2. Contact Information">
					<List
						items={[
							<>
								Privacy inquiries:{' '}
								<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
									privacy@lydra.ai
								</Link>
							</>,
							<>
								HIPAA privacy officer:{' '}
								<Link
									to="mailto:hipaa-privacy@lydra.ai"
									className="text-blue-600"
								>
									hipaa-privacy@lydra.ai
								</Link>
							</>,
							<>
								Security incidents:{' '}
								<Link to="mailto:security@lydra.ai" className="text-blue-600">
									security@lydra.ai
								</Link>
							</>,
							<>
								Data Protection Officer (EU/UK):{' '}
								<Link to="mailto:dpo@lydra.ai" className="text-blue-600">
									dpo@lydra.ai
								</Link>
							</>,
							'Mailing address: Lydra AI, 2261 Market Street, S 85553, San Francisco, CA 94114, United States.',
						]}
					/>
					<p className="mt-3">
						We will publish EU/UK representative details prior to launching
						services in those regions.
					</p>
				</Section>

				<Section title="3. Information We Collect">
					<p className="mb-3">
						The information we collect depends on how you and your organization
						use the Services.
					</p>
					<div className="space-y-3">
						<Subsection title="3.1 Account and Contact Information">
							<List
								items={[
									'Names, titles, organization affiliations.',
									'Business contact details (email, phone, mailing address).',
									'Authentication credentials (hashed passwords, passkey metadata, MFA tokens).',
									'Billing contacts, subscription tier, invoicing addresses.',
								]}
							/>
						</Subsection>
						<Subsection title="3.2 Platform Usage and Device Information">
							<List
								items={[
									'Log files (IP address, device identifiers, browser type, timestamps, referrers).',
									'Session activity, feature usage, configuration changes.',
									'Telemetry and diagnostic data tied to user or pseudonymous IDs.',
									'Cookie identifiers and analytics tags (see Section 10).',
								]}
							/>
						</Subsection>
						<Subsection title="3.3 Adverse Event and Safety Case Data">
							<List
								items={[
									'Structured pharmacovigilance fields (patient demographics, products, events, outcomes).',
									'Narratives, attachments, laboratory reports, regulatory correspondence.',
									'Voice recordings, transcripts, SMS/email content, call metadata.',
									'Medical history, treatment details, reporter data, PHI, and personal data.',
								]}
							/>
						</Subsection>
						<Subsection title="3.4 Support and Communications">
							<List
								items={[
									'Support tickets, chat logs, onboarding notes, call recordings.',
									'Feedback, surveys, beta program responses, event participation.',
									'Marketing preferences and communications.',
								]}
							/>
						</Subsection>
						<Subsection title="3.5 Payment and Commercial Information">
							<List
								items={[
									'Transaction history, purchase orders, tax IDs.',
									'Limited payment card details processed via PCI-compliant vendors (we do not store full card numbers).',
								]}
							/>
						</Subsection>
						<Subsection title="3.6 Inference Data">
							<List
								items={[
									'Risk scores, model confidence values, workflow recommendations.',
									'Quality assurance tags, audit logs, reviewer overrides.',
								]}
							/>
						</Subsection>
					</div>
				</Section>

				<Section title="4. How We Collect Information">
					<List
						items={[
							'Direct submissions via forms, uploads, APIs, or support communications.',
							'Automated collection through cookies, SDKs, telemetry, and server logs.',
							'Voice and telephony providers that deliver recordings and transcripts.',
							'Third-party integrations (safety databases, CRMs, cloud storage) configured by Customer.',
							'Public or regulatory sources when Customer instructs us to import data (e.g., FAERS, EudraVigilance).',
							'Payment processors, analytics vendors, and other service providers that assist in delivering the Services.',
						]}
					/>
				</Section>

				<Section title="5. How We Use Information">
					<p>
						When we act as a controller, we rely on the legal bases noted below
						for GDPR/UK GDPR purposes and applicable HIPAA allowances under a
						BAA.
					</p>
					<table className="border-border my-6 min-w-full border text-left text-sm">
						<thead>
							<tr>
								<th className="border-border border-b px-4 py-2">Purpose</th>
								<th className="border-border border-b px-4 py-2">
									Description
								</th>
								<th className="border-border border-b px-4 py-2">
									Legal Basis
								</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td className="border-border border-b px-4 py-2">
									Service delivery
								</td>
								<td className="border-border border-b px-4 py-2">
									Provide, operate, personalize, and maintain the Services;
									create and manage accounts; enable integrations.
								</td>
								<td className="border-border border-b px-4 py-2">
									Contract performance
								</td>
							</tr>
							<tr>
								<td className="border-border border-b px-4 py-2">
									Security and integrity
								</td>
								<td className="border-border border-b px-4 py-2">
									Authenticate users, detect fraud, enforce policies, monitor
									abuse, investigate incidents.
								</td>
								<td className="border-border border-b px-4 py-2">
									Legitimate interests; legal obligation
								</td>
							</tr>
							<tr>
								<td className="border-border border-b px-4 py-2">
									AI and model operations
								</td>
								<td className="border-border border-b px-4 py-2">
									Generate insights, automate data extraction, perform quality
									checks, fine-tune models with de-identified or authorized
									data.
								</td>
								<td className="border-border border-b px-4 py-2">
									Legitimate interests; consent (if required); HIPAA
									treatment/payment/operations under BAA
								</td>
							</tr>
							<tr>
								<td className="border-border border-b px-4 py-2">
									Support and communications
								</td>
								<td className="border-border border-b px-4 py-2">
									Respond to support requests, send service notices, provide
									training and onboarding.
								</td>
								<td className="border-border border-b px-4 py-2">
									Contract performance
								</td>
							</tr>
							<tr>
								<td className="border-border border-b px-4 py-2">
									Analytics and product improvement
								</td>
								<td className="border-border border-b px-4 py-2">
									Measure usage, conduct research, improve features, benchmark
									reliability.
								</td>
								<td className="border-border border-b px-4 py-2">
									Legitimate interests
								</td>
							</tr>
							<tr>
								<td className="border-border border-b px-4 py-2">Marketing</td>
								<td className="border-border border-b px-4 py-2">
									Send newsletters, webinars, and product updates to business
									contacts who opt in or where permitted.
								</td>
								<td className="border-border border-b px-4 py-2">
									Consent or legitimate interests
								</td>
							</tr>
							<tr>
								<td className="px-4 py-2">Compliance and legal</td>
								<td className="px-4 py-2">
									Meet regulatory obligations (HIPAA, pharmacovigilance,
									sanctions), audit, enforce agreements.
								</td>
								<td className="px-4 py-2">
									Legal obligation; legitimate interests
								</td>
							</tr>
						</tbody>
					</table>
					<p>
						We do not use PHI for marketing without explicit authorization and
						never sell personal information or PHI.
					</p>
				</Section>

				<Section title="6. How We Share Information">
					<List
						items={[
							'With subprocessors and service providers that host infrastructure, provide AI/ML capabilities, telephony, analytics, support, and billing. Subprocessors sign agreements imposing confidentiality, security, and data protection obligations aligned with HIPAA, GDPR, and our BAA commitments.',
							'With Customers when needed to fulfill instructions (for example, delivering reports, audit logs, or integration data). Authorized Users may see activity within their organization based on role-based permissions.',
							'With regulators, public health authorities, or law enforcement to comply with legal obligations or protect rights. We limit disclosures to the minimum required and notify Customers when legally allowed.',
							'In corporate transactions such as mergers, acquisitions, or financing, provided personal information remains subject to this Policy and affected Customers receive notice.',
							'With consent from the individual or Customer.',
						]}
					/>
					<p>
						We do not sell personal information or share it for cross-context
						behavioral advertising as defined by the California Consumer Privacy
						Act (CCPA/CPRA).
					</p>
				</Section>

				<Section title="7. International Data Transfers">
					<p>
						We store primary production data in the United States and may
						transfer personal information across borders to operate the
						Services. When required, we implement safeguards such as Standard
						Contractual Clauses, UK addenda, Data Privacy Framework
						participation (pending certification), and contractual and technical
						protections for subprocessors. Region-specific hosting may be
						available per Order.
					</p>
				</Section>

				<Section title="8. Security">
					<List
						items={[
							'Encryption in transit and at rest for PHI and sensitive data.',
							'Network segmentation, zero-trust access, security monitoring, and incident response playbooks.',
							'Multi-factor authentication and least-privilege access for staff, plus training and background checks where permitted.',
							'Secure development lifecycle, code reviews, penetration testing, and vulnerability management.',
							'Disaster recovery and business continuity planning.',
						]}
					/>
					<p>
						If we identify a breach affecting personal information, we will
						notify affected Customers and, where required, affected individuals
						and regulators without undue delay, consistent with the BAA and
						applicable law.
					</p>
				</Section>

				<Section title="9. Data Retention">
					<List
						items={[
							'We retain personal information for the duration of the Subscription Term and renewal periods, plus up to 30 days after termination to support data export.',
							'We retain data longer when required by law, regulation, audit obligations, or to resolve disputes (for example, pharmacovigilance record retention timelines).',
							'We may store de-identified or aggregated data indefinitely, provided it cannot reasonably identify an individual or Customer.',
						]}
					/>
				</Section>

				<Section title="10. Cookies and Similar Technologies">
					<p>
						We use first- and third-party cookies, pixels, and analytics tools
						to maintain session security, remember preferences, measure usage,
						and support marketing analytics for business contacts. Where legally
						required, we obtain consent for non-essential cookies. Users can
						manage cookies via browser settings or in-product controls. We honor
						Global Privacy Control signals by limiting analytics and advertising
						cookies for browsers that send the signal. We do not respond to
						legacy "Do Not Track" headers.
					</p>
				</Section>

				<Section title="11. AI and Automated Decision-Making">
					<List
						items={[
							`Lydra's AI features capture adverse event details, classify cases, generate narratives, and surface risk signals. Outputs are advisory; qualified personnel must review them before external use.`,
							`We maintain model versioning, training data provenance, validation artifacts, and override logs to support audit trails.`,
							`We conduct bias, accuracy, and drift assessments consistent with applicable industry practices and guidance`,
							`We use de-identified or synthetic data to improve models unless a Customer authorizes broader use of identifiable information. Customers may opt out of shared model improvements, which may limit certain features.`,
							`We do not make solely automated decisions that produce legal or similarly significant effects without human oversight.`,
						]}
					/>
				</Section>

				<Section title="12. Your Rights and Choices">
					<p>
						Rights vary by jurisdiction. We honor requests as required and
						support our Customers in fulfilling their obligations when we act as
						a processor.
					</p>
					<div className="space-y-3">
						<Subsection title="12.1 HIPAA Rights">
							When we process PHI as a Business Associate, individuals exercise
							rights (access, amendment, accounting, restrictions) through the
							covered entity Customer. We support Customers as required by the
							BAA.
						</Subsection>
						<Subsection title="12.2 GDPR and UK GDPR Rights">
							Subject to exceptions, EU/EEA and UK residents can request access,
							correction, deletion, restriction, portability, or object to
							processing. They may withdraw consent and lodge complaints with
							their supervisory authority. Submit requests to{' '}
							<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
								privacy@lydra.ai
							</Link>{' '}
							with "GDPR Request" in the subject. We verify identity and respond
							within 30 days (or the legal timeframe).
						</Subsection>
						<Subsection title="12.3 U.S. State Privacy Rights">
							Residents of California, Colorado, Connecticut, Utah, Virginia,
							and other applicable states may request access, deletion,
							correction, portability, and opt-out of targeted advertising or
							"sales." Lydra does not sell personal information or share it for
							cross-context behavioral advertising. Submit requests to{' '}
							<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
								privacy@lydra.ai
							</Link>{' '}
							with “U.S. Privacy Request” in the subject. Appeals can be sent to{' '}
							<Link
								to="mailto:privacy-appeal@lydra.ai"
								className="text-blue-600"
							>
								privacy-appeal@lydra.ai
							</Link>
							.
						</Subsection>
						<Subsection title="12.4 Marketing Preferences">
							You may unsubscribe from marketing communications by clicking the
							unsubscribe link in emails, updating preferences, or emailing{' '}
							<Link to="mailto:unsubscribe@lydra.ai" className="text-blue-600">
								unsubscribe@lydra.ai
							</Link>
							. We may still send transactional or service notices.
						</Subsection>
					</div>
				</Section>

				<Callout title="California Consumer Notice" variant="neutral">
					California residents have the rights described above. We do not sell
					or share personal information as defined by CPRA. Sensitive personal
					information is used only to provide the Services or as otherwise
					permitted by law.
				</Callout>

				<Section title="13. Children's Privacy">
					The Services are designed for enterprise use and are not directed to
					children under 18. We do not knowingly collect personal information
					from children under 13. If we learn we have done so, we will delete
					the information. Parents or guardians may contact{' '}
					<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
						privacy@lydra.ai
					</Link>{' '}
					for assistance.
				</Section>

				<Section title="14. Controller and Processor Roles">
					When Customers upload personal information, they are responsible for
					obtaining all necessary consents, approvals, and legal bases. Customer
					instructions prevail over this Policy where legally permissible.
					Customers must ensure data complies with applicable laws, including
					HIPAA's minimum necessary standard and GDPR purpose limitations.
				</Section>

				<Section title="15. Changes to This Policy">
					We may update this Policy. For material changes, we will provide at
					least 30 days' notice via email or in-product message unless immediate
					changes are required by law. Continued use of the Services after the
					effective date constitutes acceptance of the revised Policy.
				</Section>

				<Section title="16. Complaints and Escalation">
					Contact{' '}
					<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
						privacy@lydra.ai
					</Link>{' '}
					with concerns. EU/EEA residents may lodge complaints with their local
					supervisory authority. UK residents may contact the Information
					Commissioner's Office. U.S. individuals may contact the U.S.
					Department of Health and Human Services Office for Civil Rights for
					HIPAA matters or the Federal Trade Commission.
				</Section>

				<Section title="17. How to Reach Us">
					For questions about this Policy or our privacy practices, email{' '}
					<Link to="mailto:privacy@lydra.ai" className="text-blue-600">
						privacy@lydra.ai
					</Link>{' '}
					or write to Lydra AI at 2261 Market Street, Suite 85553, San
					Francisco, CA 94114, United States.
				</Section>
			</div>
		</div>
	)
}

function Title({ children }: { children: ReactNode }) {
	return <div className="text-xl font-semibold">{children}</div>
}

function Section({
	title,
	children,
}: {
	title: ReactNode
	children: ReactNode
}) {
	return (
		<section className="space-y-2">
			<h2 className="text-lg font-semibold">{title}</h2>
			<div className="space-y-3 text-sm leading-relaxed">{children}</div>
		</section>
	)
}

function Subsection({
	title,
	children,
}: {
	title: ReactNode
	children: ReactNode
}) {
	return (
		<div className="space-y-1.5">
			<h3 className="text-base font-semibold">{title}</h3>
			<div className="space-y-3 text-sm leading-relaxed">{children}</div>
		</div>
	)
}

function List({ items }: { items: ReactNode[] }) {
	return (
		<ul className="list-inside list-disc space-y-1">
			{items.map((item, i) => (
				<li key={i}>{item}</li>
			))}
		</ul>
	)
}
